import React, { useState } from 'react'
import FormikImageUpload from '../../../../components/Formik/FormikImageUpload'
import { useToast } from '../../../../components/toast/ToastContext';
import { useDocumnetPreSignedUrlMutation } from '../../../../api/contactApiSlice';
import { Formik } from 'formik';
import StyledText from '../../../../components/StyledComponents/StyledText';
import { useTheme } from '../../../../middleware/ThemeContext';
import styled from 'styled-components';
import { font, Theme } from '../../../../utils/themes';
import { WebsiteFormValues } from '../../../../types/types';
import { useLogoPreSignedUrlMutation } from '../../../../api/pageBuilderApiSlice';
import { SkeletonCell } from '../../../../components/SkeletonCell';

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}
const LogoCard = ({ companyLoading, isResponsive, setSettingConfiguration, settingConfiguration, onUploadStateChange }: { companyLoading?: boolean, isResponsive?: boolean, setSettingConfiguration: React.Dispatch<React.SetStateAction<WebsiteFormValues>>, settingConfiguration: WebsiteFormValues, onUploadStateChange?: (isUploading: boolean) => void }) => {
    const { theme } = useTheme()
    const [favIconPreSignedUrl] = useLogoPreSignedUrlMutation();
    const showToast = useToast();
    const [hasUploaded, setHasUploaded] = useState<boolean>(false);
    const [fileNameErr, setFileNameErr] = useState<string>('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    const setUploadingState = (nextValue: boolean) => {
        setIsUploading(nextValue);
        onUploadStateChange?.(nextValue);
    };

    const [uploadKey, setUploadKey] = useState(0);



    const validateLogo = (file: UploadedFile): Promise<boolean> => {
        return new Promise((resolve) => {
            // 🔴 Size check only
            if (file.file.size > 5 * 1024 * 1024) {
                showToast("Error", "Logo size must be less than 5MB", "error");
                resolve(false);
                return;
            }

            resolve(true);
        });
    };



    const handleUpload = async (
        file: UploadedFile,
        onProgress?: (percent: number) => void
    ): Promise<{ key: string }> => {
        const parts = file.name?.split(".");
        const extension = parts.pop();



        try {
            const { data } = await favIconPreSignedUrl({
                fileName: file.name,
                fileType: extension,
            });

            if (!data?.url) {
                showToast("Error", "Could not generate upload URL", "error");
                return { key: '' };
            }

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open(data.method, data.url, true);
                xhr.setRequestHeader("Content-Type", file.type);

                // 👇 Add progress tracking
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        onProgress(percent);
                    }
                };

                xhr.onload = () => {
                    if (xhr.status === 200 || xhr.status === 204) {
                        resolve({ key: data.key });
                    } else {
                        reject(new Error("Upload failed"));
                    }
                };

                xhr.onerror = () => reject(new Error("Network error"));
                xhr.send(file.file);
            });
        } catch (err) {
            showToast("Error", "Upload preparation failed", "error");
            return { key: '' };
        }
    };

    return (
        <div className=' p-3 rounded-3 w-100' style={{ border: `1px solid ${theme.CorporateTheme.colors.border}` }}>
            <Formik
                initialValues={{
                    file: [],
                    logo: settingConfiguration?.logo || '',
                    logoPreview: settingConfiguration?.logoPreview || '',
                }}
                enableReinitialize
                // validationSchema={setScopeSchema}
                onSubmit={(values) => {
                    console.log(values);
                }}
            >
                {({ handleSubmit, values, setFieldValue }) => {
                    const handleFileChange = async (files: UploadedFile[]) => {
                        if (!files.length) return;
                        const file = files[0];
                        // 🔴 STOP FLOW if invalid
                        const isValid = await validateLogo(file);
                        if (!isValid) {
                            setHasUploaded(false);
                            setUploadProgress(0);
                            setUploadingState(false);

                            // 🔥 RESET EVERYTHING
                            setFieldValue('file', []);
                            setFieldValue('logo', '');
                            setFieldValue('logoPreview', '');

                            setSettingConfiguration(prev => ({
                                ...prev,
                                logo: '',
                                logoPreview: ''
                            }));

                            // 🔥 FORCE FormikImageUpload RESET
                            setUploadKey(prev => prev + 1);

                            return;
                        }


                        const reader = new FileReader();
                        reader.onload = () => {
                            if (reader.result) {
                                setFieldValue('logoPreview', reader.result);
                                // setWebsiteDetails(prev => ({ ...prev, logoPreview: reader.result as string }));
                                setSettingConfiguration(prev => ({ ...prev, logoPreview: reader.result as string }));
                            }
                        };
                        reader.readAsDataURL(file.file);

                        setUploadingState(true);
                        setUploadProgress(0);

                        handleUpload(file, setUploadProgress)
                            .then(({ key }) => {
                                if (key) {
                                    setFieldValue('logo', key);
                                    setSettingConfiguration(prev => ({ ...prev, logo: key, logoFileName: file.name, logoFileSize: file.file.size }));
                                    setHasUploaded(true);
                                }
                            })
                            .catch(() => {
                                showToast('Error', 'Failed to upload logo', 'error');
                            })
                            .finally(() => {
                                setUploadingState(false);
                            });
                    };

                    const handleRemoveLogo = () => {
                        setFieldValue('logo', '');
                        setFieldValue('logoPreview', '');
                        setHasUploaded(false);
                        // Update parent state to send null in payload
                        setSettingConfiguration(prev => ({
                            ...prev,
                            logo: '',
                            logoPreview: '',
                            logoFileName: '',
                            logoFileSize: 0
                        }));
                    };
                    return (
                        <div>
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <StyledText fontSize={isResponsive ? '16px' : '18px'} fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>Logo</StyledText>
                                <div className='d-flex align-items-center gap-1'>
                                    <StyledText fontStyle='italic' fontSize={isResponsive ? '12px' : '14px'} fontWeight={700} textColor={theme.CorporateTheme.colors.count}>Recommended : </StyledText>

                                    {companyLoading ? <SkeletonCell height={isResponsive ? '20px' : '25px'} width='100px' /> : <FavIconRecommendationCard theme={theme}>
                                        <StyledText fontStyle='italic' fontFamily={font.lato} fontSize='12px' fontWeight={700} textColor={theme.CorporateTheme.colors.headerColor}>Less Then 5MB</StyledText>

                                    </FavIconRecommendationCard>}
                                </div>
                            </div>
                            {companyLoading ? <SkeletonCell height={'132px'} width='100%' /> : <FormikImageUpload accept=".png, .jpg, .jpeg, .webp" key={uploadKey} existingFileName={settingConfiguration?.logoFileName} existingFileSize={settingConfiguration?.logoFileSize} showRenameInput={true} name='file' onChange={handleFileChange} setHasUploaded={setHasUploaded} previewUrl={values.logoPreview} uploadProgress={uploadProgress}
                                isUploading={isUploading}
                                removeFile={handleRemoveLogo} checkError={fileNameErr} isResponsive={isResponsive} />}

                        </div>
                    )
                }}
            </Formik>
        </div>
    )
}

export const FavIconRecommendationCard = styled.div<{ theme: Theme }>`
    height: 30px;
    background: ${({ theme }) => theme.CorporateTheme.colors.headerBG}; 
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0px 15px;
`

export default LogoCard
